import React, { useEffect, useState } from 'react';
import { User, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { apiClient } from '../../services/api';
import {
  getCdnAgentUsernameError,
  getCdnShopUsernameError,
  normalizeCdnAgentUsername,
} from '../../utils/username';

export type AdminUsernameTarget =
  | { kind: 'player'; userId: string }
  | { kind: 'super_agent'; superAgentId: string }
  | { kind: 'agent'; agentId: string }
  | { kind: 'shop'; agentId: string; shopId: string }
  | { kind: 'cashier'; cashierId: string }
  | { kind: 'admin'; adminId: string };

export type AdminUpdateUsernameModalProps = {
  open: boolean;
  onClose: () => void;
  target: AdminUsernameTarget;
  currentUsername?: string | null;
  title?: string;
  onSuccess?: (data?: unknown) => void;
};

function extractError(err: unknown): string {
  const anyErr = err as { message?: string; response?: { data?: { message?: string } } };
  return anyErr?.response?.data?.message || anyErr?.message || 'Failed to update username';
}

function validateUsername(target: AdminUsernameTarget, value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return 'Username is required';
  switch (target.kind) {
    case 'agent':
    case 'super_agent':
      return getCdnAgentUsernameError(trimmed);
    case 'shop':
      return getCdnShopUsernameError(trimmed) || (trimmed ? null : 'Username is required');
    case 'player':
      if (!/^[a-zA-Z0-9_]{3,50}$/.test(trimmed)) {
        return 'Username must be 3–50 chars: letters, numbers, underscore';
      }
      return null;
    case 'cashier':
      if (!/^[A-Za-z0-9_.-]{3,80}$/.test(trimmed)) {
        return 'Username must be 3–80 chars: letters, numbers, dot, underscore, dash';
      }
      return null;
    case 'admin':
      if (!/^[a-zA-Z0-9_.-]{3,50}$/.test(trimmed)) {
        return 'Username must be 3–50 chars: letters, numbers, dot, underscore, dash';
      }
      return null;
    default:
      return null;
  }
}

function normalizeForSubmit(target: AdminUsernameTarget, value: string): string {
  if (target.kind === 'agent' || target.kind === 'super_agent') {
    return normalizeCdnAgentUsername(value);
  }
  if (target.kind === 'admin') {
    return value.trim().toLowerCase();
  }
  return value.trim();
}

async function patchUsername(target: AdminUsernameTarget, body: { username: string; reason: string }) {
  switch (target.kind) {
    case 'player':
      return apiClient.patchAdminUserUsername(target.userId, body);
    case 'super_agent':
      return apiClient.patchAdminSuperAgentUsername(target.superAgentId, body);
    case 'agent':
      return apiClient.patchAdminAgentUsername(target.agentId, body);
    case 'shop':
      return apiClient.patchAdminShopUsername(target.agentId, target.shopId, body);
    case 'cashier':
      return apiClient.patchAdminCashierUsername(target.cashierId, body);
    case 'admin':
      return apiClient.patchAdminUsername(target.adminId, body);
    default:
      throw new Error('Unknown username target');
  }
}

const AdminUpdateUsernameModal: React.FC<AdminUpdateUsernameModalProps> = ({
  open,
  onClose,
  target,
  currentUsername,
  title = 'Change username',
  onSuccess,
}) => {
  const [username, setUsername] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setUsername(currentUsername || '');
    setReason('');
    setError(null);
  }, [open, currentUsername]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    const validationError = validateUsername(target, username);
    if (validationError) {
      setError(validationError);
      return;
    }
    if (reason.trim().length < 3) {
      setError('Reason is required (min 3 characters)');
      return;
    }
    const next = normalizeForSubmit(target, username);
    setSubmitting(true);
    setError(null);
    try {
      const res = await patchUsername(target, { username: next, reason: reason.trim() });
      onSuccess?.(res);
      onClose();
    } catch (err) {
      setError(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 !mt-0 p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <Card
        className="w-full sm:max-w-md max-h-[92vh] overflow-y-auto rounded-b-none sm:rounded-lg shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader className="space-y-2 pb-2">
          <div className="flex items-start justify-between gap-2">
            <CardTitle className="text-base sm:text-lg flex items-center gap-2 min-w-0 pr-2">
              <User className="h-5 w-5 shrink-0" />
              <span className="break-words">{title}</span>
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="shrink-0"
              onClick={onClose}
              disabled={submitting}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground font-normal">
            All existing sessions for this account will be signed out.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium" htmlFor="admin-change-username">
                Username
              </label>
              <Input
                id="admin-change-username"
                className="mt-1"
                autoComplete="username"
                value={username}
                onChange={(ev) => {
                  const v = ev.target.value;
                  if (target.kind === 'agent' || target.kind === 'super_agent') {
                    setUsername(normalizeCdnAgentUsername(v));
                  } else {
                    setUsername(v);
                  }
                }}
                disabled={submitting}
              />
            </div>
            <div>
              <label className="text-sm font-medium" htmlFor="admin-change-username-reason">
                Reason
              </label>
              <Input
                id="admin-change-username-reason"
                className="mt-1"
                value={reason}
                onChange={(ev) => setReason(ev.target.value)}
                placeholder="Why are you changing this username?"
                disabled={submitting}
              />
            </div>
            {error ? <p className="text-sm text-destructive break-words">{error}</p> : null}
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end pt-1">
              <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" className="w-full sm:w-auto" disabled={submitting}>
                {submitting ? 'Updating…' : 'Update username'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminUpdateUsernameModal;
